import * as Phaser from "phaser"
import type { GameSprite } from "../types"

interface ToxicZone {
  x: number
  y: number
  radius: number
  damage: number
  duration: number
  sprite: Phaser.GameObjects.Arc
}

interface ToxicZoneSystemOptions {
  scene: Phaser.Scene
  getDogSprite: () => GameSprite
  damagePlayer: (damage: number) => void
}

const TOXIC_ZONE = {
  radius: 55,
  damagePerSecond: 0.8,
  durationMs: 2500,
  alpha: 0.4,
  fadeAlpha: 0.35,
  depth: 35,
  color: 0x22ff44,
  playerRadiusPadding: 20,
  tweenScale: 1.8,
} as const

export class ToxicZoneSystem {
  private readonly options: ToxicZoneSystemOptions
  private readonly zones: ToxicZone[] = []

  public constructor(options: ToxicZoneSystemOptions) {
    this.options = options
  }

  public update(delta: number): void {
    const dogSprite = this.options.getDogSprite()
    if (!dogSprite.active) return

    const playerX = dogSprite.x
    const playerY = dogSprite.y

    for (let i = this.zones.length - 1; i >= 0; i -= 1) {
      const zone = this.zones[i]
      zone.duration -= delta

      if (zone.duration <= 0) {
        zone.sprite.destroy()
        this.zones.splice(i, 1)
        continue
      }

      this.applyPlayerDamage(zone, playerX, playerY, delta)

      if (zone.sprite.active) {
        zone.sprite.setAlpha(
          Math.min(1, zone.duration / 500) * TOXIC_ZONE.fadeAlpha,
        )
      }
    }
  }

  public create(x: number, y: number): void {
    const scene = this.options.scene
    const zoneSprite = scene.add.circle(
      x,
      y,
      TOXIC_ZONE.radius,
      TOXIC_ZONE.color,
      TOXIC_ZONE.alpha,
    )
    zoneSprite.setDepth(TOXIC_ZONE.depth)
    zoneSprite.setBlendMode(Phaser.BlendModes.ADD)

    scene.tweens.add({
      targets: zoneSprite,
      scaleX: TOXIC_ZONE.tweenScale,
      scaleY: TOXIC_ZONE.tweenScale,
      alpha: 0,
      duration: TOXIC_ZONE.durationMs,
      ease: "Cubic.easeOut",
      onComplete: () => {
        if (zoneSprite.active) zoneSprite.destroy()
      },
    })

    this.zones.push({
      x,
      y,
      radius: TOXIC_ZONE.radius,
      damage: TOXIC_ZONE.damagePerSecond,
      duration: TOXIC_ZONE.durationMs,
      sprite: zoneSprite,
    })
  }

  private applyPlayerDamage(
    zone: ToxicZone,
    playerX: number,
    playerY: number,
    delta: number,
  ): void {
    const dx = playerX - zone.x
    const dy = playerY - zone.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance < zone.radius + TOXIC_ZONE.playerRadiusPadding) {
      this.options.damagePlayer(zone.damage * (delta / 1000))
    }
  }
}
